import React, { useId, useState } from 'react'
import FilterUbiquaOptionList from './FilterUbiquaOptionList'

/**
 * Divide un mismo filtro en tipos de opciones, por ejemplo Marcas y Producto individual.
 * Los radios de "Filtrar por" eligen la lista visible, no la cantidad de valores seleccionados:
 * las opciones siguen siendo múltiples salvo que el filtro indique `singleSelect`.
 *
 * `optionGroups` define los tipos mediante { key, label }. Cada opción debe usar el valor
 * `${groupKey}::${id}` para distinguir identificadores iguales de tipos diferentes.
 * Ejemplo: { value: 'Marcas::b1', label: 'Marcas: Marca uno' }.
 * El prefijo de la etiqueta es opcional y se oculta al mostrar la lista del grupo.
 *
 * `activeOptionGroup` controla el tipo desde el padre; sin él se usa un estado local.
 * `onOptionGroupChange` permite al padre cargar opciones remotas del tipo elegido.
 * Cambiar de tipo reinicia el buscador y cancela su envío pendiente mediante `key={groupKey}`.
 * Las selecciones se conservan en el padre, incluso las de grupos que no están visibles.
 */
export default function FilterUbiquaGroupedOptionList({
  optionGroups = [],
  activeOptionGroup,
  onOptionGroupChange,
  options = [],
  ...props
}) {
  const radioName = useId()
  const [localGroup, setLocalGroup] = useState(optionGroups[0]?.key)
  // El grupo indicado por el padre tiene prioridad; si no lo indica, usamos el estado local.
  // Si ese grupo ya no existe en optionGroups, mostramos el primero disponible
  // (o ninguno si la lista está vacía), por ejemplo cuando cambian los grupos disponibles.
  const requestedGroup = activeOptionGroup || localGroup
  const groupKey = optionGroups.some((group) => group.key === requestedGroup)
    ? requestedGroup
    : optionGroups[0]?.key
  const prefix = `${groupKey}::`
  const visibleOptions = options
    .filter((option) => String(option.value).startsWith(prefix))
    .map((option) => ({
      ...option,
      label: option.label.startsWith(`${groupKey}: `)
        ? option.label.slice(groupKey.length + 2)
        : option.label
    }))

  return (
    <div className="filter-ubiqua-mui-grouped-picker">
      <fieldset className="filter-ubiqua-mui-option-groups" disabled={props.disabled}>
        <legend>Filtrar por:</legend>
        {optionGroups.map((group) => (
          <label key={group.key} className="filter-ubiqua-mui-option-row">
            <input
              type="radio"
              name={radioName}
              className="filter-ubiqua-mui-check"
              checked={groupKey === group.key}
              onChange={() => {
                setLocalGroup(group.key)
                onOptionGroupChange?.(group.key)
              }}
            />
            <span className="filter-ubiqua-mui-option-copy">{group.label}</span>
          </label>
        ))}
      </fieldset>
      <FilterUbiquaOptionList
        {...props}
        key={groupKey}
        initialSearchQuery=""
        options={visibleOptions}
      />
    </div>
  )
}
